import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';

import type { AuthMeResponse } from '@/api/auth.api';
import {
  getSecurityPolicy,
  updateSecurityPolicy,
  type CsSecurityPolicy,
  type CsTwoFactorMode,
} from '@/api/cs/operations.api';
import { CsErrorCard, CsInlineError, CsLoading, SELECT_CLS, TEXTAREA_CLS } from '@/components/cs/PageStates';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { hasCapability } from '@/lib/capabilities';
import { useToast } from '@/hooks/useToast';

/**
 * Operations → Security (spec §3.10.4). One policy row per org — the
 * form PUTs the full diff on save (every change is audited server-side
 * with a before/after diff). Reads AND writes are manage_cs_credentials
 * on the server; the form is read-only without it.
 */

export function SecurityTab({ me }: { me: AuthMeResponse }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const canManage = hasCapability(me, 'manage_cs_credentials');

  const policyQuery = useQuery({
    queryKey: ['cs', 'security-policy'],
    queryFn: getSecurityPolicy,
    enabled: canManage,
  });

  if (!canManage) {
    return (
      <Card className="p-6 text-[13px] text-kw-muted">
        The security policy needs the manage_cs_credentials capability.
      </Card>
    );
  }
  if (policyQuery.isLoading) return <CsLoading />;
  if (policyQuery.error || !policyQuery.data) {
    return (
      <CsErrorCard
        title="Couldn't load the security policy"
        error={policyQuery.error}
        fallback="Unknown error"
      />
    );
  }

  return (
    <SecurityForm
      policy={policyQuery.data}
      onSaved={(updated) => {
        queryClient.setQueryData(['cs', 'security-policy'], updated);
        toast.show('Security policy saved.', { variant: 'success' });
      }}
    />
  );
}

function SecurityForm({
  policy,
  onSaved,
}: {
  policy: CsSecurityPolicy;
  onSaved: (policy: CsSecurityPolicy) => void;
}) {
  const [twoFactorMode, setTwoFactorMode] = useState<CsTwoFactorMode>(policy.twoFactorMode);
  const [passwordMinLength, setPasswordMinLength] = useState(String(policy.passwordMinLength));
  const [passwordRequireComplexity, setPasswordRequireComplexity] = useState(
    policy.passwordRequireComplexity,
  );
  const [passwordExpiryDays, setPasswordExpiryDays] = useState(
    policy.passwordExpiryDays === null ? '' : String(policy.passwordExpiryDays),
  );
  const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = useState(String(policy.sessionTimeoutMinutes));
  const [ssoProvider, setSsoProvider] = useState(policy.ssoProvider);
  const [ipAllowlistEnabled, setIpAllowlistEnabled] = useState(policy.ipAllowlistEnabled);
  const [ipAllowlistText, setIpAllowlistText] = useState(policy.ipAllowlist.join('\n'));

  // Re-sync when a fresh policy arrives (e.g. after another admin saved).
  useEffect(() => {
    setTwoFactorMode(policy.twoFactorMode);
    setPasswordMinLength(String(policy.passwordMinLength));
    setPasswordRequireComplexity(policy.passwordRequireComplexity);
    setPasswordExpiryDays(policy.passwordExpiryDays === null ? '' : String(policy.passwordExpiryDays));
    setSessionTimeoutMinutes(String(policy.sessionTimeoutMinutes));
    setSsoProvider(policy.ssoProvider);
    setIpAllowlistEnabled(policy.ipAllowlistEnabled);
    setIpAllowlistText(policy.ipAllowlist.join('\n'));
  }, [policy]);

  const ipAllowlist = ipAllowlistText
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l !== '');
  const allowlistLockout = ipAllowlistEnabled && ipAllowlist.length === 0;

  const mutation = useMutation({
    mutationFn: () =>
      updateSecurityPolicy({
        twoFactorMode,
        passwordMinLength: Number(passwordMinLength),
        passwordRequireComplexity,
        passwordExpiryDays: passwordExpiryDays.trim() === '' ? null : Number(passwordExpiryDays),
        sessionTimeoutMinutes: Number(sessionTimeoutMinutes),
        ssoProvider,
        ipAllowlistEnabled,
        ipAllowlist,
      }),
    onSuccess: onSaved,
  });

  return (
    <form
      onSubmit={(e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (allowlistLockout) return;
        mutation.mutate();
      }}
      className="space-y-4"
    >
      <p className="text-[13px] text-kw-muted">
        Policy of record for CS access — enforcement wiring (Stytch) lands in a later phase. Every save is
        audited with a before/after diff.
      </p>

      <Card className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1">
          <span className="text-[13px] font-medium text-kw-label">Two-factor mode</span>
          <select
            aria-label="Two-factor mode"
            value={twoFactorMode}
            onChange={(e) => {
              setTwoFactorMode(e.target.value as CsTwoFactorMode);
            }}
            className={SELECT_CLS}
          >
            <option value="off">Off</option>
            <option value="opt_in">Opt-in</option>
            <option value="required">Required</option>
          </select>
        </label>
        <Input
          label="SSO provider"
          name="cs-sso-provider"
          value={ssoProvider}
          onChange={(e) => {
            setSsoProvider(e.target.value);
          }}
        />
        <Input
          label="Password minimum length"
          name="cs-pw-min"
          type="number"
          min={8}
          max={128}
          value={passwordMinLength}
          onChange={(e) => {
            setPasswordMinLength(e.target.value);
          }}
        />
        <Input
          label="Password expiry days (blank = never)"
          name="cs-pw-expiry"
          type="number"
          min={1}
          max={3650}
          value={passwordExpiryDays}
          onChange={(e) => {
            setPasswordExpiryDays(e.target.value);
          }}
        />
        <Input
          label="Session timeout (minutes)"
          name="cs-session-timeout"
          type="number"
          min={5}
          max={10080}
          value={sessionTimeoutMinutes}
          onChange={(e) => {
            setSessionTimeoutMinutes(e.target.value);
          }}
        />
        <label className="flex items-center gap-2 pt-6 text-[13px] text-kw-shark">
          <input
            type="checkbox"
            checked={passwordRequireComplexity}
            onChange={(e) => {
              setPasswordRequireComplexity(e.target.checked);
            }}
          />
          Require password complexity
        </label>
      </Card>

      <Card className="space-y-3 p-4">
        <label className="flex items-center gap-2 text-[13px] text-kw-shark">
          <input
            type="checkbox"
            checked={ipAllowlistEnabled}
            onChange={(e) => {
              setIpAllowlistEnabled(e.target.checked);
            }}
          />
          Enable IP allowlist
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[13px] font-medium text-kw-label">Allowed IPs / CIDRs (one per line)</span>
          <textarea
            aria-label="IP allowlist"
            value={ipAllowlistText}
            onChange={(e) => {
              setIpAllowlistText(e.target.value);
            }}
            rows={4}
            spellCheck={false}
            placeholder={'203.0.113.4\n198.51.100.0/24'}
            className={TEXTAREA_CLS}
          />
        </label>
        {allowlistLockout ? (
          <p className="text-[12px] text-kw-danger-text">
            Enabling the allowlist with an empty list would lock everyone out — add at least one entry.
          </p>
        ) : null}
      </Card>

      <CsInlineError error={mutation.error} fallback="Could not save the security policy." />

      <div className="flex justify-end">
        <Button type="submit" isLoading={mutation.isPending} disabled={allowlistLockout}>
          <Save className="size-4" />
          Save policy
        </Button>
      </div>
    </form>
  );
}
